<script lang="ts">
  import type { Ref } from 'vue'
  // --- Types ---
  export interface DateRange {
    from:
      | Temporal.PlainDate
      | 'today'
      | 'epoch'
      | 'iso-start'
      | 'max'
      | 'infinity'
    to:
      | Temporal.PlainDate
      | 'today'
      | 'epoch'
      | 'iso-start'
      | 'max'
      | 'infinity'
  }
  export type DateInputCalendarModelValue =
    | Temporal.PlainDate
    | DateRange
    | null
  export interface DateCalendarProps {
    modelValue?: DateInputCalendarModelValue
    mode?: 'single' | 'range' // selection mode
    disabledDates?: (
      | Temporal.PlainDate
      | 'today'
      | 'epoch'
      | 'iso-start'
      | 'max'
      | 'infinity'
    )[]
    disabledRanges?: DateRange[]
    headerType?: 'combobox' | 'buttons'
  }
  export type ViewMode = 'days' | 'months' | 'years'

  export function useViewMode(currentMonth: Ref<Temporal.PlainDate>) {
    const viewMode = ref<ViewMode>('days')

    function selectMonth(month: number) {
      currentMonth.value = currentMonth.value.with({ month })
      viewMode.value = 'days'
    }

    function selectMonthAndYear(month: number, year: number) {
      currentMonth.value = currentMonth.value.with({ month, year })
      viewMode.value = 'days'
    }

    const baseYear = ref(currentMonth.value.year)

    function selectYear(year: number, changeViewMode = true) {
      currentMonth.value = currentMonth.value.with({ year })
      baseYear.value = year // Add this line to update the baseYear
      if (changeViewMode) {
        viewMode.value = 'months' // go back to month view
      }
    }

    function toggleViewMode() {
      if (viewMode.value === 'days') {
        viewMode.value = 'months'
      } else if (viewMode.value === 'months') {
        viewMode.value = 'years'
      } else if (viewMode.value === 'years') {
        viewMode.value = 'days'
      }
    }

    const yearRange = computed(() => {
      const maxYear = 2099
      const minYear = 2000
      return Array.from(
        { length: maxYear - minYear + 1 },
        (_, i) => minYear + i
      )
    })

    const currentYear = computed(() => baseYear.value)

    function nextYearRange() {
      baseYear.value = baseYear.value + 12
    }

    function prevYearRange() {
      baseYear.value = baseYear.value - 12
    }

    function nextMonthRange() {
      baseYear.value = baseYear.value + 1
    }

    function prevMonthRange() {
      baseYear.value = baseYear.value - 1
    }

    const monthRange = computed(() => {
      return Array.from({ length: 12 }, (_, i) =>
        currentMonth.value.with({ month: i + 1, year: baseYear.value })
      )
    })

    return {
      viewMode,
      selectMonth,
      selectYear,
      selectMonthAndYear,
      toggleViewMode,
      yearRange,
      nextYearRange,
      prevYearRange,
      monthRange,
      nextMonthRange,
      prevMonthRange,
      currentYear
    }
  }
</script>

<script setup lang="ts">
  import { ref, computed, watch } from 'vue'
  import { Temporal } from '@js-temporal/polyfill'
  import { IconChevronLeft, IconChevronRight } from '@gat-ship/icons'
  import { ComboboxField } from '../combobox-field'

  const props = withDefaults(defineProps<DateCalendarProps>(), {
    mode: 'single',
    headerType: 'combobox'
  })
  const emit = defineEmits<{
    'update:modelValue': [value: DateInputCalendarModelValue]
  }>()

  // --- State ---
  const today = Temporal.PlainDate.from(new Date().toISOString().slice(0, 10))
  const selected = ref<DateInputCalendarModelValue>(props.modelValue ?? null)
  const currentMonth = ref(
    props.modelValue && 'from' in (props.modelValue as object)
      ? resolveDate((props.modelValue as DateRange).from).with({ day: 1 })
      : props.modelValue
        ? resolveDate(props.modelValue as Temporal.PlainDate | string).with({
            day: 1
          })
        : today.with({ day: 1 })
  )

  const {
    viewMode,
    selectMonth,
    selectYear,
    yearRange,
    selectMonthAndYear,
    toggleViewMode,
    nextYearRange,
    prevYearRange,
    monthRange,
    nextMonthRange,
    prevMonthRange,
    currentYear
  } = useViewMode(currentMonth)

  watch(
    () => props.modelValue,
    (val) => {
      selected.value = val ?? null
      if (val) {
        if ('from' in (val as object)) {
          const from = resolveDate((val as DateRange).from)
          currentMonth.value = from.with({ day: 1 })
          selectYear(from.year, false)
        } else {
          const date = resolveDate(val as Temporal.PlainDate | string)
          currentMonth.value = date.with({ day: 1 })
          selectYear(date.year, false)
        }
      }
    }
  )

  // --- Calendar Computations ---
  const firstDayOfMonth = computed(() => currentMonth.value)
  const startOfWeek = computed(() => {
    const weekday = firstDayOfMonth.value.dayOfWeek // Mon=1
    return firstDayOfMonth.value.subtract({ days: weekday - 1 })
  })

  // 6 weeks × 7 days. Nested shape eliminates index math in the
  // template and keeps the iteration type-safe under
  // `noUncheckedIndexedAccess` — each inner `day` is `PlainDate`,
  // not `PlainDate | undefined`.
  const calendarWeeks = computed(() => {
    const weeks: Temporal.PlainDate[][] = []
    let d = startOfWeek.value
    for (let w = 0; w < 6; w++) {
      const week: Temporal.PlainDate[] = []
      for (let i = 0; i < 7; i++) {
        week.push(d)
        d = d.add({ days: 1 })
      }
      weeks.push(week)
    }
    return weeks
  })

  // Add week numbers computation
  const weekNumbers = computed(() => {
    const weeks: number[] = []

    for (let week = 0; week < 6; week++) {
      // Get the first day of this week (Monday)
      const weekStartDate = startOfWeek.value.add({ days: week * 7 })

      // Use Temporal's built-in weekOfYear property
      const weekNumber = weekStartDate.weekOfYear ?? 1
      weeks.push(weekNumber)
    }

    return weeks
  })

  function resolveDate(input: Temporal.PlainDate | string): Temporal.PlainDate {
    if (typeof input !== 'string') return input

    const today = Temporal.PlainDate.from(new Date().toISOString().slice(0, 10))

    switch (input) {
      case 'today':
        return today
      case 'epoch':
        return Temporal.PlainDate.from('1970-01-01')
      case 'iso-start':
        return Temporal.PlainDate.from('0001-01-01')
      case 'max':
      case 'infinity':
        return Temporal.PlainDate.from('9999-12-31')
      default:
        // fallback: try parsing as ISO string
        return Temporal.PlainDate.from(input)
    }
  }

  // --- Disabled helpers ---
  function isDisabled(day: Temporal.PlainDate): boolean {
    if (props.disabledDates?.some((d) => resolveDate(d).equals(day)))
      return true

    if (props.disabledRanges) {
      for (const rawRange of props.disabledRanges) {
        const range = {
          from: resolveDate(rawRange.from),
          to: resolveDate(rawRange.to)
        }

        if (
          Temporal.PlainDate.compare(day, range.from) >= 0 &&
          Temporal.PlainDate.compare(day, range.to) <= 0
        ) {
          return true
        }
      }
    }
    return false
  }

  // --- Selection logic ---
  let rangeDraft: Temporal.PlainDate | null = null

  function selectDate(day: Temporal.PlainDate) {
    if (isDisabled(day)) return

    if (isSelected(day)) {
      selected.value = null
      emit('update:modelValue', selected.value)
      return
    }

    if (props.mode === 'range') {
      if (!rangeDraft) {
        rangeDraft = day
        selected.value = { from: day, to: day }
        emit('update:modelValue', selected.value)
      } else {
        const cmp = Temporal.PlainDate.compare(day, rangeDraft)
        const from = cmp >= 0 ? rangeDraft : day
        const to = cmp >= 0 ? day : rangeDraft
        selected.value = { from, to }
        emit('update:modelValue', selected.value)
        rangeDraft = null
      }
    } else {
      // single selection
      selected.value = day
      emit('update:modelValue', day)
    }
  }

  // --- Highlight helpers ---
  function isSelected(day: Temporal.PlainDate): boolean {
    if (props.mode === 'single') {
      return (selected.value as Temporal.PlainDate)?.equals?.(day) ?? false
    } else if (
      props.mode === 'range' &&
      selected.value &&
      'from' in selected.value
    ) {
      const { from, to } = selected.value
      return day.equals(from) || day.equals(to)
    }
    return false
  }

  function isInRange(day: Temporal.PlainDate): boolean {
    if (props.mode === 'range' && selected.value && 'from' in selected.value) {
      const { from, to } = selected.value
      return (
        Temporal.PlainDate.compare(day, from) > 0 &&
        Temporal.PlainDate.compare(day, to) < 0
      )
    }
    return false
  }

  // --- Navigation ---
  function prevView() {
    if (viewMode.value === 'days') {
      currentMonth.value = currentMonth.value.subtract({ months: 1 })
    }
    if (viewMode.value === 'months') {
      prevMonthRange()
    }
    if (viewMode.value === 'years') {
      prevYearRange()
    }
  }
  function nextView() {
    if (viewMode.value === 'days') {
      currentMonth.value = currentMonth.value.add({ months: 1 })
    }
    if (viewMode.value === 'months') {
      nextMonthRange()
    }
    if (viewMode.value === 'years') {
      nextYearRange()
    }
  }

  function getMonthName(month: Temporal.PlainDate): string {
    return month.toLocaleString('en-US', {
      month: 'long'
    })
  }
</script>

<template>
  <div class="bg-background w-86 min-h-[362px] rounded border p-4">
    <!-- Header -->

    <div v-if="headerType === 'combobox'" class="mb-4 flex items-center gap-2">
      <ComboboxField
        class="grow"
        required
        :model-value="currentMonth.month.toString()"
        :items="
          monthRange.map((m) => ({
            title: getMonthName(m),
            value: m.month.toString()
          }))
        "
        @update:model-value="selectMonth(Number($event))"
      />
      <ComboboxField
        class="min-w-[150px]"
        required
        :model-value="currentYear.toString()"
        :items="
          yearRange.map((y) => ({ title: y.toString(), value: y.toString() }))
        "
        @update:model-value="selectYear(Number($event), false)"
      />
    </div>
    <div
      v-if="headerType === 'buttons'"
      class="mb-4 flex items-center justify-between"
    >
      <button class="rounded p-1 hover:bg-gray-100" @click="prevView">
        <icon-chevron-left class="h-5 w-5" />
      </button>
      <button
        class="text-lg font-medium hover:underline"
        @click="toggleViewMode"
      >
        <span v-if="viewMode === 'days'">
          {{
            currentMonth.toLocaleString('en-US', {
              month: 'long',
              year: 'numeric'
            })
          }}
        </span>
        <span v-else-if="viewMode === 'months'">
          {{ currentYear }}
        </span>
        <span v-else-if="viewMode === 'years'">
          {{ yearRange[0] }} - {{ yearRange[yearRange.length - 1] }}
        </span>
      </button>
      <button class="rounded p-1 hover:bg-gray-100" @click="nextView">
        <icon-chevron-right class="h-5 w-5" />
      </button>
    </div>
    <div v-if="viewMode === 'months'" class="grid grid-cols-3 gap-2">
      <button
        v-for="m in monthRange"
        :key="m.month"
        class="rounded p-2 hover:bg-gray-100"
        :class="{
          'bg-primary text-white':
            currentMonth.month === m.month && currentMonth.year === m.year,
          'hover:bg-primary/10': !isDisabled(
            Temporal.PlainDate.from({ year: m.year, month: m.month, day: 1 })
          ),
          'pointer-events-none opacity-50': isDisabled(
            Temporal.PlainDate.from({ year: m.year, month: m.month, day: 31 })
          )
        }"
        @click="selectMonthAndYear(m.month, m.year)"
      >
        {{
          new Date(currentMonth.year, m.month - 1, 1).toLocaleString('en-US', {
            month: 'short'
          })
        }}
      </button>
    </div>
    <div v-if="viewMode === 'days'" class="mb-4 text-lg font-medium">
      {{
        currentMonth.toLocaleString('en-US', {
          month: 'long',
          year: 'numeric'
        })
      }}
    </div>
    <div v-if="viewMode === 'years'" class="grid grid-cols-4 gap-2">
      <button
        v-for="y in yearRange"
        :key="y"
        class="rounded p-2 hover:bg-gray-100"
        :class="{
          'bg-primary text-white': currentMonth.year === y,
          'hover:bg-primary/10': !isDisabled(
            Temporal.PlainDate.from({ year: y, month: 12, day: 1 })
          ),
          'pointer-events-none opacity-50': isDisabled(
            Temporal.PlainDate.from({ year: y, month: 12, day: 31 })
          )
        }"
        @click="selectYear(y)"
      >
        {{ y }}
      </button>
    </div>
    <!-- Weekday headings -->
    <div
      v-if="viewMode === 'days'"
      class="mb-2 grid grid-cols-8 text-xs font-medium text-gray-500"
    >
      <!-- Week numbers column header -->
      <div class="w-6 text-center text-gray-400">Wk</div>
      <!-- Existing weekday headers -->
      <div
        v-for="d in ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']"
        :key="d"
        class="text-center"
      >
        {{ d }}
      </div>
    </div>

    <!-- Days grid with week numbers -->
    <div v-if="viewMode === 'days'" class="space-y-1">
      <div
        v-for="(week, weekIndex) in calendarWeeks"
        :key="`week-${weekNumbers[weekIndex]}`"
        class="grid grid-cols-8 gap-1 text-sm"
      >
        <!-- Week number -->
        <div
          class="flex h-10 w-6 items-center justify-center text-xs font-medium text-gray-400"
        >
          {{ weekNumbers[weekIndex] }}
        </div>

        <!-- Days for this week -->
        <button
          v-for="day in week"
          :key="day.toString()"
          :day="day"
          class="flex h-10 w-10 items-center justify-center rounded-full transition-colors"
          :disabled="isDisabled(day)"
          :class="{
            'text-muted-foreground': day.month !== currentMonth.month,
            'bg-primary text-white': isSelected(day),
            'bg-blue-100': isInRange(day),
            'bg-accent': day.equals(today) && !isSelected(day),
            'hover:bg-primary/10': !isDisabled(day),
            'cursor-not-allowed line-through opacity-60': isDisabled(day)
          }"
          @click="selectDate(day)"
        >
          {{ day.day }}
        </button>
      </div>
    </div>
    <slot name="footer" />
  </div>
</template>
